import { authPlugin, WuiFetchStrategy, AuthInitOptions, DialogPlugin, bootstrapStyles } from '@wui/common-library'
import { createPinia } from 'pinia'
import { createApp } from 'vue'

import { clearBootThemeClass, useColorTheme } from '@/features/color-theme'

import { useAppBadge } from '@/shared/composables/browser-api'
import { useLocalization } from '@/shared/composables/localization'
import { tauriMode, appBaseUrl, isElectron, ensureAppBaseUrlReady } from '@/shared/utils/import-meta'
import { showFatalErrorInApp } from '@/shared/utils/show-fatal-error-in-app'
import { tauriFetchStrategy } from '@/shared/utils/tauri'

import App from './app.vue'
import './assets/tailwind.css'
import { onAuthReadyPlugin } from './plugins/auth'
import { dayjsPlugin } from './plugins/dayjs'
import { imPlugin } from './plugins/im'
import { LinkifyBotControlPlugin } from './plugins/linkify-bot-control'
import { LinkifyPlugin } from './plugins/linkify-jira'
import { localizationPlugin } from './plugins/localization'
import { osNotificationPlugin } from './plugins/os-notification'
import useSentry from './plugins/sentry'
import { YandexMetrikaPlugin } from './plugins/yandex-metrika'
import router from './router'
import { useStore } from './store'

const app = createApp(App)

const { i18n } = useLocalization()

app.use(dayjsPlugin)

const appWithPinia = app.use(createPinia())
const appWithSentry = useSentry(appWithPinia, router)

const { localStorageColorTheme } = useColorTheme()

const authOptions: AuthInitOptions = {}
if (tauriMode()) {
  authOptions.optionalDependency = tauriFetchStrategy
  appBaseUrl()
} else if (isElectron()) {
  authOptions.url = appBaseUrl() + '/'
} else {
  // Relative to current origin so authPlugin does not build protocol-relative //api/...
  authOptions.optionalDependency = WuiFetchStrategy
  authOptions.url = '/'
}

useAppBadge().clearAppBadge()

const prepareStore = async () => {
  // Vue app.use(async) does not block mount — await DOMAINADRESS before env/SIP.
  if (tauriMode()) {
    await ensureAppBaseUrlReady()
  }
  const store = useStore()
  try {
    await store.envSettings.loadAllSettings()
  } catch (e) {
    console.error('Failed to load env settings', e)
    // Allow app to mount anyway (e.g. show login or error screen)
  }
}

const mountApp = () => {
  app.config.errorHandler = (err, _instance, info) => {
    console.error('Uncaught Vue error:', info, err)
  }

  appWithSentry
    .use(authPlugin, authOptions)
    .use(router)
    .use(imPlugin)
    .use(onAuthReadyPlugin)
    .use(LinkifyBotControlPlugin)
    .use(LinkifyPlugin)
    .use(i18n)
    .use(DialogPlugin)
    .use(localizationPlugin)
    .use(YandexMetrikaPlugin, { router })
    .use(osNotificationPlugin)
    .mount('#app')
}

const handleStartupFailure = (err: unknown, phase: 'bootstrap' | 'mountApp') => {
  const message = err instanceof Error ? err.message : String(err)
  console.error(`Application startup failed (${phase})`, err)
  showFatalErrorInApp(message)
}

Promise.all([
  prepareStore(),
  bootstrapStyles({ theme: localStorageColorTheme, useFonts: ['Inter'] }),
])
  .then(() => {
    clearBootThemeClass()
    try {
      mountApp()
    } catch (err) {
      handleStartupFailure(err, 'mountApp')
    }
  })
  .catch((err: unknown) => {
    handleStartupFailure(err, 'bootstrap')
  })
